import {Form} from './form';
import {FormTypes} from './form-types';
import {Config} from '../config';
import {MultipleContactsView} from '../view/multiple-contacts-view';
import {MultipleContactsValidator} from '../validators/multiple-contacts-validator';
import {Order} from '../config/order';
import {Util} from '../config/utils';
import { BehaviorSubject } from 'rxjs';
import { emailPattern } from '../utils/patternValidators';
import { Translations } from '../config/translations';
import { insertAfter } from '../utils/utils';

export interface ValidField {
    index: number;
    info: Array<IValidFieldInfo>;
    isValid: boolean;
}

export interface IValidFieldInfo{
    name: string;
    isValid: boolean;
    isRequired: boolean;
    isPatternValid: boolean;
    fieldId: string;
}

export class MultipleContactsForm extends Form {

    view: MultipleContactsView = new MultipleContactsView();
    type = FormTypes.MultipleContacts;
    fields = [];
    fieldGroup: Node[][] = [];
    validator: MultipleContactsValidator = new MultipleContactsValidator();

    fieldMap : ValidField[] = [];
    fieldChange = new BehaviorSubject(this.fieldMap);

    errorFieldClass = 'df-error-field';

    async init(config: Config, callbacks: any) {
        await super.init(config, callbacks);

        this.fields = this.config.settings.multipleContacts.fields;

        const productForm = this.config.widget.getProductsForm() || this.config.widget.getSubscriptionForm() ||
            this.config.widget.getAdaptiveProductsForm() || this.config.widget.getAdaptiveSubscriptionForm();

        if(!Util.isEmpty(productForm.quantityForm)){
            productForm
                .quantityForm.onQuantityChange.subscribe(this.onQuantityChange.bind(this));
        }

        this.fieldChange.subscribe(data => {
            this.markValidation();
        });

        return Promise.resolve();
    }

    async render() {
        this.view.init();
    }

    renderContact () {

        const index = this.fieldGroup.length + 1;
        const fieldSet: Node[] = [];

        this.fields.forEach((field: string)  => {
            const element = this.view.getFromHSFrame('.hs_' + field);

            if(element){
                const elementCopy = element.cloneNode(true);

                this.cleanElement(elementCopy, index);
                this.updateLabel(elementCopy, index);


                fieldSet.push(elementCopy);
            }
        });

        this.view.addGuestFields(fieldSet, index);
        this.fieldGroup.push(fieldSet);

        return Promise.resolve({fieldSet, index});
    }

    removeContact() {

        const toRemove = this.fieldGroup.pop();

        if(toRemove && toRemove[0].parentElement){
            toRemove[0].parentElement.remove();
        }
    }

    cleanElement(node: Node, index: number){

        if(node.nodeType === Node.ELEMENT_NODE){
            const element = node as Element;

            if(element.id){
                element.id = element.id + index;
            }

            const elementInput = (element as HTMLInputElement);
            if (elementInput.type &&
              (elementInput.type === 'checkbox' || elementInput.type === 'radio') &&
              elementInput.parentElement &&
              elementInput.parentElement != null &&
              elementInput.parentElement.tagName === 'LABEL'
            ) {
              elementInput.parentElement.setAttribute('for', (elementInput.parentElement.getAttribute('for') || '') + index);
            }

            if (elementInput.type && elementInput.type === 'select-one') {
                const elementSelect = (element as HTMLSelectElement);
                elementSelect.selectedIndex = 0;
            }

            element.removeAttribute('data-reactid');

            if(Object.prototype.toString.call(element) === '[object HTMLInputElement]'){
                (element as HTMLInputElement).value = '';
            }

            if (['INPUT', 'SELECT', 'TEXTAREA'].indexOf(element.tagName) !== -1) {
                element.setAttribute('autocomplete', 'off');
            }

            if(element.className === 'hs-error-msg'){
                element.innerHTML = '';
            }
        }

        if(node.hasChildNodes()){
            node.childNodes.forEach(child => {
                this.cleanElement(child, index);
            });
        }
    }

    updateLabel(node: Node, index: number){

        if(node.nodeType === Node.ELEMENT_NODE){
            const element = node as Element;

            const label = element.querySelector('label span:first-child');
            if(label){
                label.innerHTML += ' #' + (index + 1);
            }
        }
    }

    public submit(order: Order) {

        this.fieldGroup.forEach(group => {

            const fieldSet: any = {};

            group.forEach((field: Node) => {

                if(field.nodeType === Node.ELEMENT_NODE){
                    const element = field as HTMLElement;

                    const inputs = element.querySelectorAll('.hs-input') as any;

                    if (inputs.length === 1) {
                        const input = inputs[0];
                        const value = input.type !== 'checkbox' ? input['value'] : input.checked;
                        fieldSet[input.name] = value;
                    } else {
                        for (let i = 0; i < inputs.length; i++) {
                            const input = inputs[i];
                            const value = input.parentElement?.querySelector('span')?.innerText;
                            if (!fieldSet[input.name] || fieldSet[input.name].length === 0) {
                              fieldSet[input.name] = [];
                            }
                            if (value && input.checked) {
                                fieldSet[input.name].push(value);
                            }
                          }
                    }
                }
            });

            order.params.push(fieldSet);
        });

        const mainContact = order.params[0];
        const additionalContacts = order.params.slice(1);
        Object.keys(mainContact).forEach(property => {
            additionalContacts.forEach((additionalContact: any) => {
              if (additionalContact[property] === '' && property !== 'email') {
                additionalContact[property] = mainContact[property];
              }
              if (mainContact.email) {
                additionalContact['df_billing_contact'] = mainContact.email;
              }
            });
        });

        order.params = [mainContact, ...additionalContacts];

        return Promise.resolve(order);
    }

    validate() {

        return this.validator.validate({
            amount: this.paymentData.amount,
            fields: this.fieldMap
        });
    }

    onQuantityChange(quantity: number) {

        const currentSize = this.fieldGroup.length;
        const diff = quantity - currentSize - 1;

        for (let i = 0; i < Math.abs(diff); i++) {

            if(diff > 0) {
                this.renderContact().then(this.mapFields.bind(this));
            } else {
                this.removeContact();
            }
        }
    }

    mapFields(res: {fieldSet: Node[], index: number}) {
        res.fieldSet.forEach(elem => {
            const inputElement =  (elem as HTMLElement).querySelector('input');

            if(inputElement){
                const checkIfExists = this.fieldMap.find(x => x.index === res.index);

                if(checkIfExists){
                     checkIfExists.info.push(
                        {
                            name: inputElement.getAttribute('name') || '',
                            isValid: false,
                            isRequired: inputElement.hasAttribute('required'),
                            isPatternValid: true,
                            fieldId: inputElement.getAttribute('id') || ''
                        }
                    );
                } else {
                    this.fieldMap.push(
                        {
                            index: res.index,
                            isValid: true,
                            info: Array({
                                name: inputElement.getAttribute('name') || '',
                                isValid: false,
                                isRequired: inputElement.hasAttribute('required'),
                                isPatternValid: true,
                                fieldId: inputElement.getAttribute('id') || ''
                            })
                        }
                    );
                }

                inputElement.addEventListener('change', ev => {
                        const findField = this.fieldMap.find(x => x.index === res.index);
                        if(findField){
                            const findLabel = findField.info.find(x => x.name === inputElement.getAttribute('name'));
                            if(findLabel){
                                findLabel.isValid = Boolean(inputElement.value);
                                findLabel.isPatternValid = inputElement.getAttribute('name') === 'email' && findLabel.isValid ?
                                                           emailPattern.test(inputElement.value) : true;

                                this.fieldChange.next(this.fieldMap);
                            }
                        }
                });
            }
         });
         // Add this to check validation right after creating the inputs
         this.markValidation();
    }

    markValidation(){
        this.fieldMap.forEach((fieldGroup, index) => this.checkValidationErrors(fieldGroup));
    }

    checkValidationErrors(fieldGroup: ValidField) {
        const filterOptional = fieldGroup.info.filter(x => !x.isRequired);
        const filterRequired = fieldGroup.info.filter(x => x.isRequired);

        if(filterRequired.some(x => !x.isPatternValid)){
            return this.addValidationErrors(fieldGroup);
        }

        if(filterOptional.some(x=> x.isValid)){
            return filterRequired.some(x => !x.isValid) ? this.addValidationErrors(fieldGroup) : this.removeValidationErrors(fieldGroup);
        }

        if(filterRequired.every(x => !x.isValid) || filterRequired.every(x => x.isValid)){
           return this.removeValidationErrors(fieldGroup);
        }

        this.addValidationErrors(fieldGroup);
    }

    removeValidationErrors(fieldGroup : ValidField){
        const htmlField = document.querySelector('#df-guest-set-' + fieldGroup.index);
        if(htmlField){
            const errorsFields = htmlField.querySelectorAll(`.hs-form-required`);
            errorsFields.forEach(item => item.classList.add('df-hidden'));
        }

        const checkPatterns = fieldGroup.info.filter(x => x.isPatternValid);

        if(checkPatterns.length){
            checkPatterns.forEach(field => {
                const getInput = document.querySelector('#' + field.fieldId);
                if(getInput){
                    this.removePatternValidationErrors(getInput as HTMLInputElement);
                }
            });
        }
        fieldGroup.isValid = true;
    }

    addValidationErrors(fieldGroup : ValidField){
        const htmlField = document.querySelector('#df-guest-set-' + fieldGroup.index);
        if(htmlField){
            const errorsFields = htmlField.querySelectorAll(`.hs-form-required`);
            errorsFields.forEach(item => item.classList.remove('df-hidden'));
        }

        const checkPatterns = fieldGroup.info.filter(x => !x.isPatternValid);

        if(checkPatterns.length){
            checkPatterns.forEach(field => {
                const getInput = document.querySelector('#' + field.fieldId);
                if(getInput){
                    this.addPatternValidationErrors(getInput as HTMLInputElement);
                }
            });
        }
        fieldGroup.isValid = false;
    }

    addPatternValidationErrors(inputElement: HTMLInputElement){
        const parentElement = inputElement.parentElement;
        if(parentElement){
            if(parentElement.querySelector('.' + this.errorFieldClass)){
                return;
            }
        }

        const emailPatternHtml = document.createElement('p');
        emailPatternHtml.classList.add(this.errorFieldClass);
        emailPatternHtml.innerHTML = 'Email address is not valid.';
        insertAfter(emailPatternHtml, inputElement);
    }

    removePatternValidationErrors(inputElement: HTMLInputElement){
        const parentElement = inputElement.parentNode;
        if(parentElement){
            const emailPatternHtml = parentElement.querySelectorAll('.' + this.errorFieldClass);
            emailPatternHtml.forEach(el => parentElement.removeChild(el));
        }
    }

}
